import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { redirectToLogin } from '../../utils/auth-redirect';

const LAST_PATH_KEY = 'reporting_app_last_path';

/**
 * Reads the last visited path from localStorage.
 */
export function getLastPath(): string | null {
    try {
        return localStorage.getItem(LAST_PATH_KEY);
    } catch {
        return null;
    }
}

/**
 * Saves the current path to localStorage so SmartRedirect can restore it after re-auth.
 */
export function saveLastPath(path: string): void {
    try {
        localStorage.setItem(LAST_PATH_KEY, path);
    } catch {
        // localStorage unavailable — silently ignore
    }
}

/**
 * Redirects the user to the best available page after login:
 * 1. Last visited page (if user still has access)
 * 2. First accessible page by sortOrder
 * 3. "No access" message if no pages are available
 */
export function SmartRedirect() {
    const { user, isLoading } = useAuth();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    if (!user) {
        redirectToLogin(window.location.pathname + window.location.search);
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    const accessiblePages = user.accessiblePages.filter(p => p.canView);

    // Try last visited path first
    const lastPath = getLastPath();
    // A sub-route (/bank-balance/weekly) counts as its page — the installed app starts on '/'
    // and should land back on the quick-entry view, not on the page's main view.
    if (lastPath && accessiblePages.some(p => p.pageUrl === lastPath || lastPath.startsWith(`${p.pageUrl}/`))) {
        return <Navigate to={lastPath} replace />;
    }

    // Fall back to first accessible page (sorted by sortOrder from API)
    if (accessiblePages.length > 0) {
        return <Navigate to={accessiblePages[0].pageUrl} replace />;
    }

    // No accessible pages at all
    return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">No Access</h1>
                <p className="text-gray-600">
                    You do not have access to any pages. Please contact your administrator.
                </p>
            </div>
        </div>
    );
}
